/**
 * The proposal for one redline over one anchored read: the step the live
 * preview and Apply share, so what the user sees before clicking is what is
 * written when they do.
 *
 * The redline is read once into a `RedlineSource`, from its markup or from
 * the card's texts. For every read, the source is arranged for the tracker's
 * placement (on the paragraph, or beside it as new paragraphs), the live texts
 * are taken from the read, and the `ProposalBuilder` produces the package: the
 * live paragraphs redlined toward the redline's text, three-way merged where
 * the user kept editing.
 */
import { DocumentComments } from "@common/utils/redline/ooxml/document-comments";
import { OoxmlNumbering } from "@common/utils/redline/ooxml/ooxml-numbering";
import { OoxmlOutlineLevels } from "@common/utils/redline/ooxml/ooxml-outline-levels";
import { ParagraphXml } from "@common/utils/redline/ooxml/paragraph-xml";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { NO_TABLE_CHANGES, type TableOperationsApplyResult } from "@common/utils/redline/table-operations";
import { ProposalBuilder, type ProposalOutcome } from "@common/utils/redline/proposal-builder";
import { type OwnChangeWriting, RevertChangeReask, type RevertChangeReaskReason, type RevertTargetsOfTheRun } from "@common/utils/redline/redline-engine";
import { TextEditPlanner } from "@common/utils/redline/text-edit-planner";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import type { DocumentParagraph } from "@common/utils/redline/wire/document-paragraph";
import type { LinkInstruction } from "@common/utils/redline/wire/link-instruction";
import { type RedlinePayload, type RedlineTextProposal, textsPayloadFrom } from "@common/utils/redline/wire/redline-payload";
import { ON_PARAGRAPH, type RedlinePlacement, relocatesBesideParagraph } from "@common/utils/redline/wire/redline-placement";
import { type PlacedRedline, RedlineSource } from "@common/utils/redline/redline-source";

import { UserFacingError } from "../../errors";
import type { AnchoredRead } from "../anchor-tracker";
import type { TableRead } from "../word-document";
import { type CardInsertionGroup, cardInsertionGroupsOf } from "./card-piece-placement";
import { PreviewPackage } from "./preview-package";

/** One change the composer lays over a table: the card as a proposal, and the stamp its revisions carry, which tells its marks from the others'. */
export interface ChangeComposedOverTable {
    proposal: AnchoredProposal;
    stampedAt: string;
}

/**
 * What a throw of the card's own change means to the card's own preview: the build
 * failed, and the card shows it (`LiveProposalState.cardBuildFailed`), so the throw
 * goes on to the caller as it came.
 */
function rethrowTheCardsOwnBuildError(error: unknown): never {
    throw error;
}
import { TableChangePreview } from "./table-change-preview";

export interface BuiltProposal {
    outcome: ProposalOutcome;
    placed: PlacedRedline;
    textsOnPage: string[];
}

/** A user's edit of a text card, written over the proposal's layer under the user's name with its own, later stamp. */
export interface ProposalEditLayer {
    textRedline: RedlineTextProposal;
    author: string;
    stampedAt: string;
    /**
     * The card as the edit box opened on it: the proposal's texts as the page read
     * when the user began the edit, the base each of the user's texts is merged from.
     *
     * WHY
     *   The box opens on the paragraph as the document reads, so a word changed on
     *   the page since the card was proposed is in the box. Merged from the card's
     *   stored text instead, an edit that puts that word back reads as no change and
     *   is dropped.
     *
     * NULL
     *   An edit saved before the box recorded what it opened on: each text is merged
     *   from the proposal's own text, as every edit was before.
     */
    textRedlineWhenOpened: RedlineTextProposal | null;
}

/** One cell paragraph a card names, with the proposal that writes that paragraph alone. */
export interface CellProposal {
    bookmark: string;
    proposal: AnchoredProposal;
}

/** A card's preview over the live tables: the composed package and the ids of every paragraph those tables span. */
export interface TablePreview {
    packageOoxml: string;
    tableParagraphUniqueLocalIds: string[];
}

/** A package with the user's edit laid over it, and how many changes that layer alone made. */
export interface LayeredEditPackage {
    packageOoxml: string;
    layerChangeCount: number;
}

/** A text a card writes, with the bookmark of the live paragraph a card paragraph is written to; null for a new paragraph or an added cell, which have no bookmark of their own. */
interface CardTextInReadingOrder {
    bookmark: string | null;
    text: string;
}

/** One of the proposal's texts paired with the user's text for it; `edited` is null when the edit's card holds fewer texts than the proposal's. */
interface EditLayerEntry {
    bookmark: string | null;
    proposed: string;
    /**
     * The text the user's edit of this position was made over: what the edit box
     * opened on, or the proposal's text for an edit that did not record it.
     *
     * EXAMPLE
     *   proposed     "13.1 Either party may terminate ... on sixty (60) days"
     *   editedOver   "13.1 Either party may promptly terminate ... on sixty (60) days"   (an earlier card's word on the page)
     *   edited       "13.1 Either party may terminate ... on forty-five (45) days"        (the user struck it)
     */
    editedOver: string;
    edited: string | null;
}

/** How a card is built over a read of Word (`AnchoredProposal.build`). */
interface AnchoredBuildOptions {
    anchored: AnchoredRead;
    placement: RedlinePlacement;
    stampedAt: string;
    /**
     * The marks an Apply All run laid on the page the read comes from (`ProposalInput.revertTargetsOfTheRun`).
     *
     * NULL
     *   Absent for every build outside a run's fold: the build marks its own reverts.
     */
    revertTargetsOfTheRun?: RevertTargetsOfTheRun;
}

/** How a card is built over a package the caller already holds (`AnchoredProposal.buildOverPackage`). */
interface PackageBuildOptions {
    rangeOoxml: string;
    placement: RedlinePlacement;
    stampedAt: string;
    /** NULL: as `AnchoredBuildOptions.revertTargetsOfTheRun`. */
    revertTargetsOfTheRun?: RevertTargetsOfTheRun;
}

/** What one live paragraph of the package read when the edit was made and what it should read after the user's layer; the same text when the layer leaves it alone. */
interface EditLayerTarget {
    textWhenProposed: string;
    textProposed: string;
}

/** What the card says when neither path of the region fork reads the intended text, so nothing was written. */
export const CHANGE_NOT_PLACED_EXACTLY_MESSAGE = "This change could not be placed in the document exactly, so it was not written.";

/**
 * The sentence a card shows for each way the engine hands a revert back
 * (`RevertChangeReask`). The engine's own message names change ids and lists the
 * paragraph's changes for the agent; a user is told what it means for the document alone.
 *
 * WHY one sentence each
 *   Every reason said "no longer in the document as it was when the card was proposed".
 *   For §9.1(j) and §10.1 of Dell's MRA the change was still there, read alike with
 *   another (2026-10-23): the user was told to look for a change that had not moved.
 */
export const REVERT_REFUSED_USER_MESSAGES: Record<RevertChangeReaskReason, string> = {
    unknownChange: "The tracked change this card reverts is no longer in the document.",
    staleChange: "The tracked change this card reverts reads differently now than when the card was proposed.",
    ambiguousChange: "The document holds more than one tracked change that reads like the one this card reverts, and the card cannot tell which one it meant.",
    textOnInsertion: "This card rewrites the words of an insertion it takes back; an insertion can only be struck.",
    nothingToRevert: "The words this card strikes are already struck in the document.",
    paragraphStruck: "This paragraph is struck as a whole; reverting a change inside a struck paragraph is not supported yet.",
};

export class AnchoredProposal {
    private readonly source: RedlineSource;

    /**
     * `ownChangeWriting` says how an edit or a revert that reaches the card author's own
     * pending change is written when this proposal is built: a preview's proposal draws
     * it as a tracked change, so the card shows the words it deletes or takes back;
     * Apply's proposal writes it as Word does (`OwnChangeWriting`).
     */
    constructor(readonly redline: RedlinePayload, readonly ownChangeWriting: OwnChangeWriting) {
        this.source = RedlineSource.fromPayload(redline);
    }

    /** False for a redline with no paragraph text at all, which cannot be written as paragraphs. */
    get hasParagraphs(): boolean {
        return this.source.allChangesAcceptedTexts.length > 0;
    }

    /** False for a redline with neither paragraph text nor a row or table change, which Apply has nothing to write for. */
    get hasChangesToApply(): boolean {
        return this.hasParagraphs || this.tableChanges !== null;
    }

    /**
     * Whether the card itself asks to change any paragraph's words or to add a
     * paragraph, whatever a build over some read of the page finds.
     *
     * CASES
     *   - a markup card                                   -> false: only its build says what it changes
     *   - a text card adding a paragraph                  -> true
     *   - a text card with a paragraph whose words change -> true; a change of spacing alone writes nothing
     *
     * EXAMPLE
     *   "" -> "By: "                  -> true
     *   "Title:  Manager" -> "Title: Manager" -> false
     */
    changesParagraphText(): boolean {
        if (this.redline.kind !== "texts") return false;
        return this.redline.insertions.length > 0 || this.redline.paragraphs.some((paragraph) => !TextEditPlanner.requestWritesNothing(paragraph.textWhenProposed, paragraph.textProposed));
    }

    /**
     * The row and table changes the card asks for, written after its paragraphs.
     *
     * NULL
     *   A markup card (its rows, if any, are in its markup), or a text card whose
     *   table changes are all empty.
     */
    get tableChanges(): TableOperationsApplyResult | null {
        if (this.redline.kind !== "texts") return null;
        const changes = this.redline.tableChanges;
        const asksForNothing = changes.rowInsertions.length === 0 && changes.rowDeletions.length === 0 && changes.tableInsertions.length === 0;
        return asksForNothing ? null : changes;
    }

    /**
     * How many row and table changes the card asks for, countable before Word is
     * read, so a card whose only change is a row is never shown as already applied.
     *
     * EXAMPLE
     *   one row inserted, one table nested -> 2; a card with no table change -> 0
     */
    get tableChangeCount(): number {
        const changes = this.tableChanges;
        if (changes === null) return 0;
        return changes.rowInsertions.length + changes.rowDeletions.length + changes.tableInsertions.length;
    }

    /**
     * The preview lines for the card's table changes, naming each anchor row by the
     * text the live range shows for its paragraph (`TableChangePreview`).
     *
     * RETURNS
     *   [] when the card asks for no table change.
     */
    tableChangePreviewLines(anchored: AnchoredRead): string[] {
        const changes = this.tableChanges;
        if (changes === null) return [];
        const liveParagraphs = new OoxmlRange(anchored.rangeOoxml).documentParagraphs(DocumentComments.none(), OoxmlNumbering.none(), OoxmlOutlineLevels.none());
        return TableChangePreview.lines({
            edits: changes,
            anchorTextOf: (bookmark) => liveParagraphs.find((paragraph) => paragraph.bookmarkNames.includes(bookmark))?.allChangesAcceptedText ?? null,
        });
    }

    /**
     * The one package the card previews and, once applied, documents, with
     * the paragraphs of every table it was composed from: the
     * built proposal's paragraphs with the card's table changes written in,
     * each affected table as the page store last read it (`tableReadOf`),
     * the cells the page puts the card's paragraphs in named by `cellBookmarks`
     * (`PreviewPackage.compose`).
     *
     * RETURNS
     *   The built package as is, with no table paragraphs, when the card writes into no table.
     */
    previewPackage(tableReadOf: (bookmarkName: string) => TableRead | null, cellBookmarks: string[], built: BuiltProposal, stampedAt: string): TablePreview {
        const changes = this.tableChanges;
        const editedCellBookmarks = cellBookmarks;
        /**
         * WHEN the card changes no row and edits no cell
         *   CONSEQUENCE
         *     the built package is the preview: a paragraph card has no table to compose.
         */
        if (changes === null && editedCellBookmarks.length === 0) return { packageOoxml: built.outcome.packageOoxml, tableParagraphUniqueLocalIds: [] };
        const tableChanges = changes ?? NO_TABLE_CHANGES;
        const anchors = [
            ...tableChanges.rowDeletions.map((deletion) => deletion.rowOfBookmark),
            ...tableChanges.rowInsertions.map((insertion) => insertion.afterRowOfBookmark),
            ...tableChanges.tableInsertions.map((tableInsertion) => tableInsertion.afterBookmark),
            ...editedCellBookmarks,
        ];
        const tableReadByBookmark = new Map<string, TableRead | null>();
        for (const bookmark of anchors) {
            if (tableReadByBookmark.has(bookmark)) continue;
            tableReadByBookmark.set(bookmark, tableReadOf(bookmark));
        }
        const tableReads = Array.from(tableReadByBookmark.values()).filter((read): read is TableRead => read !== null);
        const tableParagraphUniqueLocalIds = Array.from(new Set(tableReads.flatMap((read) => read.paragraphUniqueLocalIds)));
        const distinctTablePackages = Array.from(new Set(tableReads.map((read) => read.packageOoxml)));
        /**
         * WHEN every anchor sits in one live table
         *   CONSEQUENCE
         *     the preview is that table with the card's changes written in, the one
         *     composition the drawer draws too, so the two surfaces never differ.
         *
         * WHEN there is no table to read, or the anchors span tables
         *   CONSEQUENCE
         *     the built package is composed with whatever table parts were read: a
         *     table added after a body paragraph is written into the paragraph package.
         */
        if (distinctTablePackages.length === 1) return { packageOoxml: AnchoredProposal.composeChangesOverTable(distinctTablePackages[0], [{ proposal: this, stampedAt }], rethrowTheCardsOwnBuildError), tableParagraphUniqueLocalIds };
        return {
            packageOoxml: PreviewPackage.compose({
                paragraphPackageOoxml: built.outcome.packageOoxml,
                tableChanges,
                insertionGroups: this.cellInsertionGroups(),
                tablePackageOf: (bookmark) => tableReadByBookmark.get(bookmark)?.packageOoxml ?? null,
                author: this.redline.author,
                stampedAt,
            }),
            tableParagraphUniqueLocalIds,
        };
    }

    /**
     * The one composer of this change, or these changes, over this table: every
     * surface that draws a card's change over a table reads it, the chat card's live
     * and kept previews (`previewPackage`), the drawer's table groups and its conflict
     * groups (`tableGroupChangesOf`), and the Apply All panel's rows through them.
     *
     * CASES
     *   - one change              -> the card's cells redlined in place, its rows added or deleted, its added
     *                                lines placed after the lines they follow, a table it adds where it asked
     *   - several                 -> each change composed over the table the ones before it left, in order, so
     *                                two cards' changes to one cell both show, each under its own author label,
     *                                and a conflict group draws both readings
     *   - a change whose build
     *     throws                  -> handed to `onChangeThatThrows` with the change that threw, and the next
     *                                change composed over the table as it stood before it: the card's own
     *                                preview rethrows it, so the card shows its build error; the drawer
     *                                reports it, holds that card back from its Apply and draws the rest
     *   - none                    -> the table as read
     *
     * WHY one composer
     *   The chat card, the drawer and a conflict group each composed their own way and
     *   showed different tables for one card; the conflict group dropped its contested
     *   cards and showed the untouched table (page finding #16, 2026-10-16).
     *
     * WHY the composer takes what becomes of a change that throws
     *   One table card whose build threw took the whole drawer down (WORD-ADDIN-VJ). Left
     *   to each caller, the drawer re-wrote this loop with a catch of its own; here, the one
     *   loop holds the one rule, and each caller says what a throw means to it.
     */
    static composeChangesOverTable<ChangeOverTable extends ChangeComposedOverTable>(tablePackageOoxml: string, changes: readonly ChangeOverTable[], onChangeThatThrows: (error: unknown, changeThatThrew: ChangeOverTable) => void): string {
        let composedTablePackage = tablePackageOoxml;
        for (const change of changes) {
            try {
                composedTablePackage = change.proposal.composeOverTable(composedTablePackage, change.stampedAt);
            } catch (error) {
                onChangeThatThrows(error, change);
            }
        }
        return composedTablePackage;
    }

    /**
     * This card's changes written into one live table: the cells the card edits
     * redlined in place, its rows added or deleted, its added lines placed after the
     * lines they follow, and a table it adds placed where it asked. One step of the
     * one composer (`composeChangesOverTable`).
     *
     * CASES
     *   - every cell the card names is in the table -> those cells are rebuilt over the table's own text, so typing
     *                                                  since the card was proposed merges in, then moved back into place
     *   - a cell the card names is gone              -> no cell is rewritten; the row and table changes still land
     */
    private composeOverTable(tablePackageOoxml: string, stampedAt: string): string {
        const cellParagraphPackage = this.cellParagraphPackageIn(tablePackageOoxml);
        const builtCells = cellParagraphPackage === null ? null : this.buildOverPackage({ rangeOoxml: cellParagraphPackage, placement: ON_PARAGRAPH, stampedAt });
        return PreviewPackage.compose({
            paragraphPackageOoxml: builtCells === null ? OoxmlRange.packageAround("") : builtCells.outcome.packageOoxml,
            tableChanges: this.tableChanges ?? NO_TABLE_CHANGES,
            insertionGroups: this.cellInsertionGroups(),
            tablePackageOf: () => tablePackageOoxml,
            author: this.redline.author,
            stampedAt,
        });
    }

    /**
     * One proposal per card paragraph that names a cell, for writing a card into a
     * table cell by cell.
     *
     * WHY
     *   A card over several cells reads back from Word as a table fragment, and
     *   `Range.insertOoxml(replace)` over a range spanning cells puts the whole
     *   fragment into the first cell as a nested table. Each cell's paragraph is a
     *   plain paragraph range, so each is written on its own.
     *
     * RETURNS
     *   [] for a markup card.
     */
    cellProposals(): CellProposal[] {
        if (this.redline.kind !== "texts") return [];
        const texts = this.redline;
        return texts.paragraphs.flatMap((paragraph) => (paragraph.bookmark === null
            ? []
            : [{
                bookmark: paragraph.bookmark,
                proposal: new AnchoredProposal(textsPayloadFrom({
                    author: texts.author,
                    paragraphs: [paragraph],
                    insertions: [],
                    tableChanges: NO_TABLE_CHANGES,
                    cardKind: "paragraphs",
                    /** The cell's own reverts come with it, so a reverted cell previews and writes the revert like a body paragraph. */
                    reverts: texts.reverts.filter((revert) => revert.bookmark === paragraph.bookmark),
                }), this.ownChangeWriting),
            }]));
    }

    /**
     * The paragraphs a card adds, by the paragraph each group follows: the one
     * placement the cell writer writes them by, after the writes of the cells
     * themselves (`CellChangeWriter`), and the preview draws them by
     * (`PreviewPackage.compose`), so the two never differ (`cardInsertionGroupsOf`).
     *
     * WHY apart from `cellProposals`
     *   Each cell proposal carries one cell paragraph and no insertion, so a cell card's
     *   new paragraphs ("Name: Morgan Chen" after the blank last line of a signature
     *   cell) were written by no door at all.
     *
     * RETURNS
     *   [] for a markup card, and for a text card that adds no paragraph.
     */
    cellInsertionGroups(): CardInsertionGroup[] {
        if (this.redline.kind !== "texts") return [];
        return cardInsertionGroupsOf(this.redline.insertions);
    }

    /**
     * The card built over one live cell paragraph, alone in its package as the cell writer
     * takes it out of Word's read (`CellChangeWriter.cellParagraphOf`).
     *
     * WHY the paragraph alone
     *   Word returns a cell paragraph wrapped in its table and row. Built as read,
     *   the package would go back into the cell as a nested table; built over the
     *   paragraph alone, it goes back as the paragraph.
     */
    buildOverLiveParagraph(paragraphOoxml: string, stampedAt: string): BuiltProposal {
        return this.buildOverPackage({ rangeOoxml: paragraphOoxml, placement: ON_PARAGRAPH, stampedAt });
    }

    /**
     * The card's cell paragraphs as the table holds them now, in the card's order,
     * as one package for the builder to redline.
     *
     * RETURNS
     *   null for a markup card, and when any cell the card names is not in the table:
     *   the builder pairs card texts with paragraphs by position, so a missing cell
     *   would misalign every text after it.
     */
    private cellParagraphPackageIn(tablePackageOoxml: string): string | null {
        if (this.redline.kind !== "texts") return null;
        const tableRange = new OoxmlRange(tablePackageOoxml);
        const tableParagraphs = tableRange.documentParagraphs(DocumentComments.none(), OoxmlNumbering.none(), OoxmlOutlineLevels.none());
        const cellParagraphXmls = this.redline.paragraphs.map((cardParagraph) => {
            const index = tableParagraphs.findIndex((tableParagraph) => cardParagraph.bookmark !== null && tableParagraph.bookmarkNames.includes(cardParagraph.bookmark));
            return index < 0 ? null : tableRange.paragraphXml(index);
        });
        if (cellParagraphXmls.some((xml) => xml === null)) return null;
        return OoxmlRange.packageAround(cellParagraphXmls.join(""));
    }

    /**
     * Whether the card writes into a table as the page stands: a row or table
     * change, or a paragraph the page puts in a cell.
     *
     * WHY the cells come from the page store
     *   Whether a card writes into a table is decided by where its paragraphs stand,
     *   never by the card's label; the store resolves each bookmark's table once, for
     *   every card and the drawer alike, so a cell edit proposed by any tool previews
     *   as the table it sits in.
     */
    writesIntoTable(cellBookmarks: string[]): boolean {
        return this.tableChanges !== null || cellBookmarks.length > 0;
    }

    get textsProposed(): string[] {
        return this.source.allChangesAcceptedTexts;
    }

    build({ anchored, placement, stampedAt, revertTargetsOfTheRun }: AnchoredBuildOptions): BuiltProposal {
        const pageRange = new OoxmlRange(anchored.rangeOoxml);
        const textsOnPage = pageRange.allChangesAcceptedTexts();
        /**
         * How many of the page's paragraphs are the document's, the paragraphs a card placed
         * beside one goes beside.
         *
         * CASES
         *   - Word's export as given (`AnchoredRead.wordsExportAsGiven`) -> those before Word's closing
         *                                                                  paragraph, which the write keeps
         *   - every other read, the document's paragraphs alone          -> all of them
         */
        const documentParagraphCountOnPage = anchored.wordsExportAsGiven === true ? pageRange.paragraphCountBeforeWordsClosingParagraph() : textsOnPage.length;
        const placed = this.source.placedFor({ placement, textsOnPage, bookmarkNamesOnPage: pageRange.bookmarkNamesCarried(), documentParagraphCountOnPage });
        const outcome = AnchoredProposal.builtForTheCard(() => new ProposalBuilder(this.redline.author, stampedAt).build({
            currentRangeOoxml: anchored.rangeOoxml,
            deliveredAs: "rangeReplacement",
            textsProposed: placed.allChangesAcceptedTexts,
            /**
             * WHEN placement relocates beside the paragraph
             * CONSEQUENCE
             *   coveredSpans is null: the redline becomes sibling paragraphs, not edits inside
             *   the anchored paragraph
             *
             * WHY
             *   The in-paragraph span map only applies when merging back into the same paragraph.
             *   Once placement splits into sibling paragraphs, there is no span map to merge against.
             */
            coveredSpans: relocatesBesideParagraph(placement) ? null : anchored.coveredSpans,
            insertions: placed.insertions,
            textsWhenProposed: placed.textsBeforeAuthor,
            /**
             * CASES
             *   - a text card        -> the pending changes it reverts, applied before its text redlines
             *   - a markup card      -> none: prod's writer never reverts
             */
            reverts: this.redline.kind === "texts" ? this.redline.reverts : [],
            /**
             * CASES
             *   - a text card   -> each of its paragraphs' text by bookmark, so a revert of an insertion
             *                      knows whether the card writes words into the gap it leaves
             *   - a markup card -> none: it carries no reverts
             *
             * NULL
             *   A card paragraph with no bookmark (one the card adds) has no entry: no revert names it.
             *
             * EXAMPLE
             *   paragraphs [{ _pay, "…within forty-five (45) days of receipt." }, { null, "New clause." }]
             *   -> { "_pay" => "…within forty-five (45) days of receipt." }
             */
            cardTextByBookmark: this.redline.kind === "texts"
                ? new Map(this.redline.paragraphs.flatMap((cardParagraph) => (cardParagraph.bookmark === null ? [] : [[cardParagraph.bookmark, cardParagraph.textProposed] as const])))
                : new Map(),
            linkInstructionsByBookmark: AnchoredProposal.linkInstructionsByBookmarkOf(this.redline),
            ownChangeWriting: this.ownChangeWriting,
            revertTargetsOfTheRun,
            documentParagraphCount: documentParagraphCountOnPage,
        }));
        return { outcome, placed, textsOnPage };
    }

    /**
     * Runs the builder for a card and turns an engine re-ask over one of the card's
     * reverts into the sentence the card shows.
     *
     * WHEN the engine hands a revert back while the proposal is built
     *   CONSEQUENCE
     *     nothing was written yet, so the card gets a `UserFacingError`: the apply
     *     path shows it as a refusal, never as a partial apply, and the live preview
     *     shows the same sentence. The re-ask rides along as the cause, with the
     *     engine's own message, which names change ids and the paragraph's changes
     *     for the agent that reads them and is never shown to a user.
     */
    private static builtForTheCard(buildProposal: () => ProposalOutcome): ProposalOutcome {
        let outcome: ProposalOutcome;
        try {
            outcome = buildProposal();
        } catch (error) {
            if (error instanceof RevertChangeReask) throw new UserFacingError(REVERT_REFUSED_USER_MESSAGES[error.reason], error);
            throw error;
        }
        /**
         * WHEN the region fork ran and neither path read the intended text
         *   CONSEQUENCE
         *     nothing is written; the preview shows the sentence and Apply refuses with it.
         */
        if (outcome.regionVerdict !== null && outcome.regionVerdict.used === null) throw new UserFacingError(CHANGE_NOT_PLACED_EXACTLY_MESSAGE);
        return outcome;
    }

    /**
     * What the card says becomes of each link whose words it changes, by the bookmark of
     * the paragraph the link sits in (`ProposalInput.linkInstructionsByBookmark`), so the
     * engine keeps the link, writes it anew to another address, or writes the words plain.
     *
     * CASES
     *   - a text card   -> each named paragraph's instructions
     *   - a markup card -> none: prod's writer reads no instruction
     *
     * EMPTY
     *   A card paragraph with no bookmark (one the card adds) holds no link of the document,
     *   and a card stored before instructions existed carries none: no entry for either.
     *
     * EXAMPLE
     *   paragraphs [{ _result_prev_signed, linkInstructions [change "the Fee Schedule"] }, { null, [] }]
     *   -> { "_result_prev_signed" => [change "the Fee Schedule"] }
     */
    private static linkInstructionsByBookmarkOf(redline: RedlinePayload): ReadonlyMap<string, readonly LinkInstruction[]> {
        const linkInstructionsByBookmark = new Map<string, readonly LinkInstruction[]>();
        /** WHEN the card is markup CONSEQUENCE it names no link. */
        if (redline.kind !== "texts") return linkInstructionsByBookmark;
        for (const cardParagraph of redline.paragraphs) {
            /** WHEN the paragraph is one the card adds, or names no link CONSEQUENCE no entry. */
            if (cardParagraph.bookmark === null || cardParagraph.linkInstructions === undefined || cardParagraph.linkInstructions.length === 0) continue;
            linkInstructionsByBookmark.set(cardParagraph.bookmark, cardParagraph.linkInstructions);
        }
        return linkInstructionsByBookmark;
    }

    /**
     * The proposal before Word has been read: the page is taken to read as it did
     * when the redline was proposed, so the card shows its redline the moment it
     * arrives. Same `build`, over a range of plain paragraphs of `textsBeforeAuthor`
     * instead of the anchored read; the first live read replaces it.
     *
     * WHY plain paragraphs
     *   Nothing of the document is known yet, so the paragraphs carry no properties
     *   and no bookmark; the package previews and is never written.
     */
    buildBeforeRead({ placement, stampedAt }: { placement: RedlinePlacement; stampedAt: string }): BuiltProposal {
        const rangeOoxml = OoxmlRange.packageAround(this.source.textsBeforeAuthor.map((text) => ParagraphXml.plainFromText({ text, templateParagraphXml: "" })).join(""));
        return this.buildOverPackage({ rangeOoxml, placement, stampedAt });
    }

    /**
     * The same `build` over a package the caller already holds instead of an anchored
     * read: whole paragraphs, no paragraph ids, nothing repaired. Used before Word has
     * been read, and by Apply All to fold one card over the running package of a
     * paragraph several cards redlined.
     */
    buildOverPackage({ rangeOoxml, placement, stampedAt, revertTargetsOfTheRun }: PackageBuildOptions): BuiltProposal {
        return this.build({ anchored: { rangeOoxml, paragraphIds: [], uniqueLocalIds: [], coveredSpans: [], bookmarkRepairCount: 0, elapsedMs: 0 }, placement, stampedAt, revertTargetsOfTheRun });
    }

    /**
     * The user's edit of this redline, written over the built package as a second
     * layer of tracked changes under the user's own name, so the preview and its
     * legend show the proposal's words and the user's words as two authors.
     *
     * WHEN the card has an edit
     *   CONSEQUENCE
     *     Each paragraph of the built package the proposal wrote is redlined again
     *     toward the user's text for it; every other paragraph is left as it is. The
     *     layer takes `edit.stampedAt`, a stamp later than the proposal's, so
     *     relabelling the proposal's stamp to "Proposed by this redline" leaves the
     *     user's revisions under the user's name.
     *
     * WHY over the package, not inserted again
     *   The package after the first layer already holds the proposal's inserted
     *   paragraphs, so the edit is laid over live paragraphs, one desired text per
     *   live paragraph, and nothing is inserted a second time or realigned by similarity.
     *
     * EXAMPLE
     *   proposal paragraph  "pay within thirty (30) days" -> "pay within forty-five (45) days"
     *   edit of it          "pay within forty (40) days"
     *   package after layer 1 reads "pay within forty-five (45) days" with "thirty (30)" struck by the proposal
     *   layer 2 strikes "forty-five (45)" and inserts "forty (40)" under the user's name
     *
     * RETURNS
     *   The built proposal with the layered package and both layers' change counts; `stampedAt` stays the proposal's.
     */
    layerEdit(built: BuiltProposal, edit: ProposalEditLayer): BuiltProposal {
        const layered = this.layerEditOverPackage(built.outcome.packageOoxml, built.outcome.stampedAt, edit);
        return {
            ...built,
            outcome: { ...built.outcome, packageOoxml: layered.packageOoxml, changeCount: built.outcome.changeCount + layered.layerChangeCount },
        };
    }

    /**
     * `layerEdit` over a package the caller holds: the composed preview, which
     * already carries the rows and tables the card adds, so the user's edits to
     * those cells are laid over them too, and the live preview re-layers a changed
     * edit over the package it last composed without reading Word again.
     *
     * Each live paragraph is paired with the proposal's text it is the written form
     * of (`editLayerEntryIndexFor`), and the layer is built with the text the edit box
     * opened on at that position as what the paragraph read when the edit was made,
     * so the builder's three-way merge carries the user's edit over whatever was typed
     * into the paragraph since the box opened, exactly as Apply carries the edited
     * card over the page.
     *
     * CASES, per live paragraph
     *   - the written form of one of the proposal's texts -> from the text the box opened on toward the user's text for it
     *   - anything else                                   -> its own text on both sides: unchanged
     *
     * EXAMPLE, a paragraph typed into after the card was proposed
     *   proposal    "provide the services with reasonable skill" -> "provide the services with professional skill"
     *   edit of it  "provide the services with professional care"
     *   page now    "promptly provide the services with reasonable skill"
     *   layer 1     "promptly provide the services with professional skill"
     *   layer 2     "promptly provide the services with professional care", "care" under the user's name
     *
     * RETURNS
     *   The layered package and the count of changes the user's layer alone made.
     */
    layerEditOverPackage(packageOoxml: string, proposalStampedAt: string, edit: ProposalEditLayer): LayeredEditPackage {
        const editedTexts = AnchoredProposal.cardTextsInReadingOrder(edit.textRedline);
        /**
         * CASES
         *   - the edit recorded what the box opened on -> its texts, read with the same walk as the proposal's
         *   - an edit saved before it recorded that     -> null: each position is merged from the proposal's text
         */
        const textsTheBoxOpenedOn = edit.textRedlineWhenOpened === null ? null : AnchoredProposal.cardTextsInReadingOrder(edit.textRedlineWhenOpened);
        /**
         * The proposal's texts, the edit's and the box's opening texts line up one to
         * one: the box opened on the proposal's card and the edit was made in it.
         *
         * FALLBACK
         *   A position the opening card holds no text for is merged from the proposal's
         *   text at that position.
         */
        const unusedEntries: EditLayerEntry[] = this.redline.kind === "texts"
            ? AnchoredProposal.cardTextsInReadingOrder(this.redline).map((cardText, index) => ({
                bookmark: cardText.bookmark,
                proposed: cardText.text,
                editedOver: textsTheBoxOpenedOn?.[index]?.text ?? cardText.text,
                edited: editedTexts[index]?.text ?? null,
            }))
            : [];
        const packageRange = new OoxmlRange(packageOoxml);
        const liveParagraphs = packageRange.documentParagraphs(DocumentComments.none(), OoxmlNumbering.none(), OoxmlOutlineLevels.none());
        /** `proposalStampedAt` is the stamp `packageOoxml` was built under, so only this card's own insertions count as the proposal's. */
        const insertedByProposal = packageRange.paragraphsInsertedBy(this.redline.author, proposalStampedAt);
        const targets = liveParagraphs.map((liveParagraph, liveIndex): EditLayerTarget => {
            const liveText = liveParagraph.allChangesAcceptedText;
            const unchanged: EditLayerTarget = { textWhenProposed: liveText, textProposed: liveText };
            const matchIndex = AnchoredProposal.editLayerEntryIndexFor(liveParagraph, insertedByProposal[liveIndex], unusedEntries);
            /**
             * WHEN the paragraph is the written form of none of the proposal's texts
             *   CONSEQUENCE
             *     It stays as it is: a row the card did not add, or a paragraph beside the card's.
             */
            if (matchIndex < 0) return unchanged;
            const [matched] = unusedEntries.splice(matchIndex, 1);
            /**
             * WHEN the edit holds no text at this position
             *   CONSEQUENCE
             *     Nothing is laid over the paragraph: the edit was made over a shorter card than the proposal's.
             */
            if (matched.edited === null) return unchanged;
            return { textWhenProposed: matched.editedOver, textProposed: matched.edited };
        });
        const layered = new ProposalBuilder(edit.author, edit.stampedAt).build({
            currentRangeOoxml: packageOoxml,
            deliveredAs: "rangeReplacement",
            textsProposed: targets.map((target) => target.textProposed),
            coveredSpans: null,
            insertions: [],
            textsWhenProposed: targets.map((target) => target.textWhenProposed),
            /** The edit layer lays the user's words over the proposal's layer, whose reverts are already in the package. */
            reverts: [],
            ownChangeWriting: this.ownChangeWriting,
        });
        return { packageOoxml: layered.packageOoxml, layerChangeCount: layered.changeCount };
    }

    /**
     * The proposal's text a live paragraph of the package is the written form of, as
     * an index into `entries`; the caller takes each entry once.
     *
     * CASES
     *   - a card paragraph whose bookmark the live paragraph carries              -> that entry, however the paragraph reads now
     *   - otherwise a card paragraph the live paragraph reads as, word for word   -> that entry: a package without bookmarks, built before Word was read
     *   - otherwise a new paragraph or an added cell the live paragraph reads as,
     *     when the proposal inserted the live paragraph                           -> that entry: the package holds it exactly as proposed
     *   - neither                                                                 -> -1
     *
     * WHY the bookmark before the text
     *   Once the paragraph was typed into after the card was proposed, layer one wrote
     *   the three-way merge of the proposal with the page, so the paragraph reads as
     *   none of the card's texts; its bookmark still names it.
     *
     * WHY only a paragraph the proposal inserted may be a new paragraph or an added cell
     *   Neither has a bookmark, and a paragraph the document already held can read the
     *   same: an existing "3%" cell above an added "3%" cell, or the "3%" cell of a row
     *   an earlier card by the same user added and Word still shows as tracked. Read in
     *   document order, that cell would take the entry and the user's "4%" with it,
     *   while Apply writes the added cell. The proposal's own insertions carry its
     *   author and its stamp; the earlier card's carry an earlier stamp.
     */
    private static editLayerEntryIndexFor(liveParagraph: DocumentParagraph, liveParagraphInsertedByProposal: boolean, entries: EditLayerEntry[]): number {
        const byBookmark = entries.findIndex((entry) => entry.bookmark !== null && liveParagraph.bookmarkNames.includes(entry.bookmark));
        if (byBookmark >= 0) return byBookmark;
        const readsAsEntry = (entry: EditLayerEntry): boolean => WordTokenizer.readsTheSame(entry.proposed, liveParagraph.allChangesAcceptedText);
        return entries.findIndex((entry) => readsAsEntry(entry) && (entry.bookmark !== null || liveParagraphInsertedByProposal));
    }

    /**
     * Every text a card writes, in the order its paragraphs appear in the package the
     * card previews as: its paragraphs, each with the bookmark of the live paragraph it
     * is written to, then its new paragraphs, then the cells of every row it adds, then
     * the cells of every table it adds, row by row. The proposal's card and the user's
     * edit of it are read with the same walk, so position `n` of one is the edit of
     * position `n` of the other.
     *
     * EXAMPLE
     *   paragraphs      [{ bookmark: "_fees", textProposed: "Fees." }, { bookmark: "_premium", textProposed: "Premium support" }]
     *   insertions      ["Payment is due on receipt."]
     *   rowInsertions   [{ rows: [["Annual uplift cap", "3%"]] }]
     *   -> "_fees" "Fees.", "_premium" "Premium support", then without a bookmark: "Payment is due on receipt.", "Annual uplift cap", "3%"
     */
    private static cardTextsInReadingOrder(card: RedlineTextProposal): CardTextInReadingOrder[] {
        const withoutBookmark = (text: string): CardTextInReadingOrder => ({ bookmark: null, text });
        return [
            ...card.paragraphs.map((paragraph): CardTextInReadingOrder => ({ bookmark: paragraph.bookmark, text: paragraph.textProposed })),
            ...card.insertions.map((insertion) => withoutBookmark(insertion.textProposed)),
            ...card.tableChanges.rowInsertions.flatMap((rowInsertion) => rowInsertion.rows.flat()).map(withoutBookmark),
            ...card.tableChanges.tableInsertions.flatMap((tableInsertion) => tableInsertion.rows.flat()).map(withoutBookmark),
        ];
    }

    /** The redline's paragraphs as a package of whole insertions, for placing after a range that has no live counterpart. */
    insertedParagraphsPackage(stampedAt: string): string {
        return new ProposalBuilder(this.redline.author, stampedAt).insertedParagraphsPackage(this.source.besideParagraphXmls);
    }
}
